import type { PropsWithChildren } from 'react';
import React, { Children, useRef } from 'react';
import type { SwiperOptions } from 'swiper';
import { Navigation as NavigationModule, Scrollbar } from 'swiper';
import { SwiperSlide } from 'swiper/react';

import type { Maybe } from '@/lib/types';
import { getBreakpointSize } from '@/theme';

import { Navigation } from './Navigation';

import * as S from './BoutiqueSlider.styles';
export interface BoutiqueSliderProps {
  children: React.ReactNode;
  disableGutters?: boolean;
  variantColor?: 'light' | 'dark';
}

export const BoutiqueSlider = ({
  children,
  disableGutters,
  variantColor = 'light',
}: BoutiqueSliderProps) => {
  return (
    <S.Wrapper
      data-test-id='BoutiqueSliderWrapper'
      disableGutters={disableGutters}
      variantColor={variantColor}
    >
      {children}
    </S.Wrapper>
  );
};

const Header = ({ children }: PropsWithChildren) => {
  return <S.Title data-test-id='BoutiqueSliderTitle'>{children}</S.Title>;
};

export interface SlideProps {
  breakpoints?: Maybe<SwiperOptions['breakpoints']>;
  children: React.ReactNode;
  isMobileView?: boolean;
  slidesPerView?: Maybe<number>;
  spaceBetween?: number;
}

const Slider = ({
  breakpoints,
  children,
  isMobileView,
  slidesPerView: initialSlidesPerView,
  spaceBetween = 20,
}: SlideProps) => {
  const slidesPerView = initialSlidesPerView ?? 4;
  // const itemsCount = Children.count(children);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const defaultBreakpoints = {
    [getBreakpointSize('xs')]: {
      slidesPerView: 1.2,
      spaceBetween: spaceBetween,
    },
    [getBreakpointSize('sm')]: {
      slidesPerView: 1.3,
      spaceBetween: spaceBetween,
    },
    [getBreakpointSize('md')]: {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
    },
    [getBreakpointSize('lg')]: {
      slidesPerView: slidesPerView,
      spaceBetween: spaceBetween,
    },
  };
  return (
    <>
      <S.StyledSwiper
        $hideNavigation={slidesPerView < 3}
        $separator={false}
        breakpoints={breakpoints || defaultBreakpoints}
        centeredSlidesBounds
        data-test-id='BoutiqueSliderStyledSwiper'
        modules={[NavigationModule, Scrollbar]}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        scrollbar={slidesPerView >= 3 || isMobileView}
        slidesPerView={2.45}
        spaceBetween={spaceBetween}
        watchSlidesProgress
      >
        {Children.map(
          children,
          (child, index) => (
            // prettier-ignore
            <SwiperSlide key={index} data-test-id={`BoutiqueSliderSwiperSlide-${index}`}>{child}</SwiperSlide>
          ), //NOSONAR
        )}
      </S.StyledSwiper>
      {slidesPerView >= 3 && (
        <Navigation
          ref={nextRef}
          data-test-id='BoutiqueSliderNavigationNext'
          type='next'
        />
      )}
      {slidesPerView >= 3 && (
        <Navigation
          ref={prevRef}
          data-test-id='BoutiqueSliderNavigationPrev'
          type='prev'
        />
      )}
    </>
  );
};

BoutiqueSlider.Header = Header;
BoutiqueSlider.Slider = Slider;

BoutiqueSlider.displayName = 'BoutiqueSlider';
